// The word-counting logic, with no React in it, so the same code runs in the page and in a
// Web Worker (see wordCounter.worker.ts) -- QA measured a ~2.4 s main-thread freeze counting
// 300,000 words (WC-9), so big inputs are now counted off the main thread.

const hasSegmenter = typeof Intl !== "undefined" && typeof (Intl as any).Segmenter === "function";

// This file's own tsconfig target has no explicit value (defaults to
// ES3) despite esnext in `lib`, so a /u-flag regex literal fails to
// compile here -- the RegExp constructor isn't subject to that same
// literal-syntax check. One shared instance, reused below.
const LETTER_OR_NUMBER = new RegExp("[\\p{L}\\p{N}]", "u");

// A run of CJK/Thai/Hangul characters -- scripts written without spaces
// between words, where Intl.Segmenter's word-boundary data is genuinely
// needed because whitespace-splitting can't find word boundaries at all.
const UNSPACED_SCRIPT = /[\u3040-\u30ff\u3400-\u9fff\uac00-\ud7af\u0e00-\u0e7f]/;

// Han ideographs and Japanese kana. Word and Google Docs count each of these characters as one word, so
// we do too (policy: per character, letters only; the punctuation between them is not counted).
// Thai keeps dictionary word segmentation, and Hangul is written with spaces, so both stay as before.
const PER_CHAR_SCRIPT = new RegExp("[\\u3040-\\u30ff\\u3400-\\u9fff\\uf900-\\ufaff\\u{20000}-\\u{2fa1f}]", "u");
const PER_CHAR_SCRIPT_G = new RegExp(PER_CHAR_SCRIPT.source, "gu");

let wordSegmenter: any = null;
function getWordSegmenter(): any {
  return wordSegmenter ?? (wordSegmenter = new (Intl as any).Segmenter(undefined, { granularity: "word" }));
}

export function countWords(t: string): number {
  const trimmed = t.trim();
  if (!trimmed) return 0;
  // Confirmed directly, not assumed: Segmenter's own word-granularity
  // segmentation splits INSIDE a whitespace-delimited token wherever it
  // hits a hyphen or a dot -- "e-books" came back as two separate
  // word-like segments ("e", "books"), "a.b@c.org" as two ("a.b",
  // "c.org"). For scripts that use spaces, a human word is a
  // whitespace-delimited token, not a Segmenter word-like run, so split
  // on whitespace first and only reach for Segmenter inside a token
  // that actually needs word-boundary data (CJK/Thai with no spaces at
  // all) -- most tokens just need "does this contain a letter or digit".
  const tokens = trimmed.split(/\s+/);
  if (!hasSegmenter) return tokens.reduce((n, tok) => n + (PER_CHAR_SCRIPT.test(tok) ? (tok.match(PER_CHAR_SCRIPT_G)?.length ?? 0) : LETTER_OR_NUMBER.test(tok) ? 1 : 0), 0);
  const seg = getWordSegmenter();
  let count = 0;
  for (const tok of tokens) {
    if (UNSPACED_SCRIPT.test(tok) || PER_CHAR_SCRIPT.test(tok)) {
      // Each Han/kana character is a word; whatever else is in the token (Thai, Latin, digits) is segmented as before.
      const cjk = tok.match(PER_CHAR_SCRIPT_G)?.length ?? 0;
      const others = cjk ? tok.replace(PER_CHAR_SCRIPT_G, " ") : tok;
      count += cjk + Array.from(seg.segment(others)).filter((s: any) => s.isWordLike).length;
    } else if (LETTER_OR_NUMBER.test(tok)) {
      count += 1;
    }
  }
  return count;
}

const DOTTED_INITIALISM = new RegExp("^(?:\\p{L}|Ph)(?:\\.\\p{L})+$", "u");
const ABBR = /^(Dr|Mr|Mrs|Ms|Prof|Jr|Sr|Mt|Gen|Gov|Col|Lt|Capt|Rev|St|vs|etc|e\.g|i\.e|a\.m|p\.m|U\.S|No|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)$/i;
const UPPER_LETTER = new RegExp("^\\p{Lu}$", "u");
const DIGITS_ONLY = new RegExp("^\\p{N}+$", "u");
const UPPER_AFTER_SPACE = new RegExp("^\\s+\\p{Lu}", "u");
// Words that normally begin a sentence: "in the U.S. It was fun" ends there, "the U.S. Army" does not.
const SENTENCE_STARTER = /^\s+(It|He|She|They|We|You|I|The|This|That|These|Those|There|Then|But|And|So|Our|My|His|Her|Their|In|On|At|After|Before|When|If|As|A|An)\b/;
const TERMINATORS = ".!?\u3002\uFF01\uFF1F"; // . ! ? 。 ！ ？
const FULL_WIDTH_TERMINATORS = "\u3002\uFF01\uFF1F";

/**
 * The sentences of `trimmed` as [start, end) offsets into it (end excludes nothing: the slice includes its terminator).
 * countSentences is this list's length, so the count and the sentence-length view can never disagree.
 */
function sentenceSpans(trimmed: string): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  if (!trimmed) return spans;
  // Confirmed directly, not assumed: Intl.Segmenter's own sentence-
  // boundary algorithm requires an uppercase letter after a terminator
  // to treat it as a real sentence end -- "first sentence here. second
  // sentence here. third one!" came back as a single segment. Dropped
  // Segmenter for this one entirely in favor of a direct scan: a
  // terminator run ends a sentence when followed by whitespace or
  // end-of-text (CJK terminators don't take a following space by
  // convention, so they end a sentence unconditionally), except when
  // the dot sits between two letters/digits with no space (URLs,
  // emails, decimals) or the word before it is a known abbreviation.
  let start = 0;
  for (let i = 0; i < trimmed.length; i++) {
    if (!TERMINATORS.includes(trimmed[i])) continue;
    let j = i;
    while (j + 1 < trimmed.length && TERMINATORS.includes(trimmed[j + 1])) j++;
    const prevChar = trimmed[i - 1];
    const nextChar = trimmed[j + 1];
    if (
      trimmed[i] === "." &&
      prevChar &&
      nextChar &&
      LETTER_OR_NUMBER.test(prevChar) &&
      LETTER_OR_NUMBER.test(nextChar)
    ) {
      i = j;
      continue;
    }
    const before = trimmed.slice(start, i).trim();
    const lastWord = before.split(/\s+/).pop() ?? "";
    const rest = trimmed.slice(j + 1);
    const abbr = lastWord.replace(/\.$/, "");
    // "5 p.m. Is it ..." and "tea, coffee, etc. They ..." end a sentence when a capital letter follows;
    // other abbreviations (Mr., U.S.) are followed by names, so they never end one.
    // A dotted initialism (U.S., D.C., U.K., Ph.D.) ends a sentence only when a typical sentence opener follows:
    // "in Washington D.C. on Jan. 5" and "the U.S. Army" go on, "in the U.S. It was fun" ends.
    const isInitialism = DOTTED_INITIALISM.test(abbr);
    const endsLikeWord = (/^(a\.m|p\.m|etc)$/i.test(abbr) && UPPER_AFTER_SPACE.test(rest)) || (isInitialism && SENTENCE_STARTER.test(rest));
    // A single capital letter before the dot is an initial ("J. R. R. Tolkien"), not the end of a sentence.
    const isInitial = trimmed[i] === "." && j === i && UPPER_LETTER.test(abbr) && abbr !== "I" && UPPER_AFTER_SPACE.test(rest);
    if (isInitial) {
      i = j;
      continue;
    }
    if (trimmed[i] === "." && (ABBR.test(abbr) || isInitialism) && !endsLikeWord) {
      i = j;
      continue;
    }
    const isFullWidth = FULL_WIDTH_TERMINATORS.includes(trimmed[i]);
    if (rest.length === 0 || /^\s/.test(rest) || isFullWidth) {
      spans.push([start, j + 1]);
      start = j + 1;
      i = j;
    }
  }
  if (trimmed.slice(start).trim().length > 0) spans.push([start, trimmed.length]);
  return spans;
}

export function countSentences(t: string): number {
  return sentenceSpans(t.trim()).length;
}

/** Each sentence's text and word count, in order (same split as countSentences). */
export function splitSentenceTexts(t: string): Array<{ text: string; words: number }> {
  const trimmed = t.trim();
  return sentenceSpans(trimmed).map(([a, b]) => { const text = trimmed.slice(a, b).trim(); return { text, words: countWords(text) }; });
}

export function countGraphemes(t: string): number {
  if (!hasSegmenter) return t.length;
  const seg = new (Intl as any).Segmenter(undefined, { granularity: "grapheme" });
  return Array.from(seg.segment(t)).length;
}

export function formatReadingTime(words: number): string {
  if (words === 0) return "0 sec";
  if (words < 200) return "< 1 min";
  const totalMinutes = Math.round(words / 200);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return mins === 0 ? `${hours} h` : `${hours} h ${mins} min`;
}

export interface TextStats {
  words: number;
  chars: number;
  charsNoSpace: number;
  sentences: number;
  paragraphs: number;
}

export function computeStats(text: string): TextStats {
  const words = countWords(text);
  return {
    words,
    chars: countGraphemes(text),
    charsNoSpace: countGraphemes(text.replace(/\s/g, "")),
    sentences: countSentences(text),
    paragraphs: text.trim() ? text.split(/\n\n+/).filter((p) => p.trim()).length : 0,
  };
}

/** Above this many characters the counting runs in a Web Worker. */
export const WORKER_THRESHOLD_CHARS = 200_000;

/** Speaking time at about 130 words a minute (a relaxed presentation or audiobook pace). */
export const SPEAKING_WPM = 130;
export function formatSpeakingTime(words: number): string {
  if (words === 0) return "0 sec";
  const totalSeconds = Math.round((words / SPEAKING_WPM) * 60);
  if (totalSeconds < 60) return `${totalSeconds} sec`;
  const minutes = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  if (minutes < 60) return secs === 0 ? `${minutes} min` : `${minutes} min ${secs} sec`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

const STOP_WORDS = new Set(("a an and are as at be but by for from had has have he her his i if in into is it its me my no not of on or our she so " +
  "than that the their them then there these they this to was we were what when which who will with you your").split(" "));

export interface KeywordRow { word: string; count: number; percent: number }

/**
 * The most used words (up to `limit`), with their share of all words. Common filler words (the, and, of ...) are left out of
 * the list (unless includeCommon) but still count in the percentage's total. Words are lower-cased; one-letter words and plain numbers are skipped.
 * Spaced scripts only: Chinese and Japanese text has no word boundaries to count here.
 */
export function topKeywords(text: string, limit = 10, includeCommon = false): KeywordRow[] {
  const total = countWords(text);
  if (!total) return [];
  const counts = new Map<string, number>();
  const re = new RegExp("[\\p{L}\\p{M}\\p{N}]+(?:['\u2019-][\\p{L}\\p{M}\\p{N}]+)*", "gu");
  for (const m of Array.from(text.toLowerCase().matchAll(re))) {
    const w = m[0];
    if (w.length < 2 || (!includeCommon && STOP_WORDS.has(w)) || DIGITS_ONLY.test(w) || PER_CHAR_SCRIPT.test(w)) continue;
    counts.set(w, (counts.get(w) ?? 0) + 1);
  }
  return Array.from(counts, ([word, count]) => ({ word, count, percent: Math.round((count / total) * 1000) / 10 }))
    .sort((a, b) => b.count - a.count || (a.word < b.word ? -1 : 1))
    .slice(0, limit);
}
